/**
 * Shared pickup / ship-or-pickup availability helpers.
 * Used on PDP (via snippet) and Shop the Look modal (after content inject).
 */
(function () {
  if (!window.inventoryParser) {
    window.inventoryParser = {
      locationMap: {
        'gid://shopify/Location/21433221174': 'Houston Curbside Pickup',
        'gid://shopify/Location/21364015158': 'Inwood Curbside Pickup',
        'gid://shopify/Location/60916564022': 'Southlake Curbside Pickup',
        'gid://shopify/Location/62768611382': 'San Antonio Curbside Pickup',
        'gid://shopify/Location/63259443254': 'Austin Curbside Pickup',
      },

      /**
       * @param {string | number | null | undefined} variantId
       * @param {{ inventory?: Record<string, Record<string, number>> } | null | undefined} inventoryJson
       */
      loadData(variantId, inventoryJson) {
        let selectedVariantId = variantId;
        if (selectedVariantId === undefined || selectedVariantId === null) {
          if (window.location.search.includes('variant')) {
            selectedVariantId = window.location.search.split('variant=')[1].split('&')[0];
          }
        }
        return inventoryJson && inventoryJson.inventory
          ? inventoryJson.inventory[`gid://shopify/ProductVariant/${selectedVariantId}`]
          : undefined;
      },

      /**
       * @param {Record<string, number> | null | undefined} inventoryData
       * @param {HTMLElement} dropdownElem
       * @param {string} [selectedLoc]
       * @param {string} [itemWeight]
       * @param {string} [selectId]
       * @param {boolean | string} [pickupOnly]
       * @param {{ isCart?: boolean, onChange?: (event: Event) => void }} [options]
       */
      createDropdown(inventoryData, dropdownElem, selectedLoc = '', itemWeight, selectId, pickupOnly, options = {}) {
        if (!dropdownElem) return;

        const resolvedSelectId = selectId || 'pickup-select';
        const label = document.createElement('label');
        label.htmlFor = resolvedSelectId;
        label.textContent = 'Shipping Option';
        label.className = 'visually-hidden';

        const selectBox = document.createElement('select');
        selectBox.id = resolvedSelectId;
        selectBox.name = 'properties[Shipping Option]';
        selectBox.setAttribute('aria-label', 'Shipping Option');

        if (options.isCart && typeof options.onChange === 'function') {
          selectBox.onchange = options.onChange;
        }

        const isPickupOnly =
          pickupOnly === true ||
          pickupOnly === 'true' ||
          itemWeight === '9999' ||
          itemWeight === '9999.0' ||
          itemWeight === 9999;

        if (!isPickupOnly) {
          const shipOption = document.createElement('option');
          shipOption.text = 'Ship To Me';
          shipOption.value = 'Ship to me';
          if (selectedLoc === 'Ship to me') shipOption.selected = true;
          selectBox.appendChild(shipOption);
        }

        const safeInventory = inventoryData || {};
        for (const locId of Object.keys(this.locationMap)) {
          const stockCount = typeof safeInventory[locId] !== 'undefined' ? safeInventory[locId] : 0;
          const option = document.createElement('option');
          option.value = `pickup ${this.locationMap[locId]}`;
          if (selectedLoc === locId || selectedLoc === `pickup ${this.locationMap[locId]}`) {
            option.selected = true;
          }
          option.text = `${this.locationMap[locId]} - ${stockCount}`;
          if (stockCount <= 0) option.disabled = true;
          selectBox.appendChild(option);
        }

        dropdownElem.innerHTML = '';
        dropdownElem.appendChild(label);
        dropdownElem.appendChild(selectBox);
      },
    };
  }

  /**
   * Refresh a live pickup holder from current DOM (survives product-details morph).
   * @param {HTMLElement} holder
   * @param {{ variantId?: string|number|null, variantAvailable?: boolean|null } | null} [update]
   */
  function refreshPickupHolder(holder, update = null) {
    const container = holder.closest('.pickup-locations') || holder;
    if (!(container instanceof HTMLElement)) return;

    let inventoryJson = {};
    try {
      inventoryJson = JSON.parse(container.getAttribute('data-inventory-json') || '{}');
    } catch {
      inventoryJson = {};
    }

    const selectId = holder.id
      ? holder.id.replace('pickup_select_holder', 'pickup-select')
      : 'pickup-select';
    const defaultVariantId = container.getAttribute('data-default-variant-id') || undefined;
    const pickupOnly = container.getAttribute('data-pickup-only') === 'true';

    // Sold-out variants hide the whole box, heading included (the notify form takes its place)
    const isPickupBox = container !== holder;
    const isSoldOut = Boolean(update && (!update.variantId || !update.variantAvailable));
    // Compact boxes keep their place and disable the select instead, so the layout doesn't shift
    const keepWhenSoldOut = container.hasAttribute('data-keep-when-sold-out');
    if (update && isPickupBox && !keepWhenSoldOut) {
      container.hidden = isSoldOut;
    }

    if (isSoldOut && keepWhenSoldOut) {
      const currentSelect = holder.querySelector('select');
      if (currentSelect instanceof HTMLSelectElement) {
        clearStoreUnavailable(container, currentSelect);
        currentSelect.disabled = true;
      }
      return;
    }

    if (isSoldOut) {
      holder.innerHTML = '';
      holder.classList.remove('pickup-select');
      holder.style.setProperty('display', 'none', 'important');
      return;
    }

    const form =
      holder.closest('product-form-component') ||
      holder.closest('form') ||
      container.closest('product-form-component') ||
      container.closest('[data-product-grid-content]')?.querySelector('product-form-component');
    const variantInput = form?.querySelector('[name="id"]');
    const resolvedVariantId =
      update?.variantId ||
      (variantInput instanceof HTMLInputElement && variantInput.value) ||
      defaultVariantId ||
      window.ShopifyAnalytics?.meta?.selectedVariantId ||
      undefined;

    if (!resolvedVariantId) {
      holder.innerHTML = '';
      holder.classList.remove('pickup-select');
      holder.style.setProperty('display', 'none', 'important');
      return;
    }

    let inventoryData = window.inventoryParser.loadData(resolvedVariantId, inventoryJson);
    if (!inventoryData || typeof inventoryData === 'undefined') {
      inventoryData = {};
    }

    holder.style.removeProperty('display');
    if (!holder.classList.contains('pickup-select')) {
      holder.classList.add('pickup-select');
    }

    window.inventoryParser.createDropdown(
      inventoryData,
      holder,
      '',
      undefined,
      selectId,
      pickupOnly
    );

    if (!holder.querySelector('select')) {
      holder.innerHTML = '';
      holder.classList.remove('pickup-select');
      holder.style.setProperty('display', 'none', 'important');
      return;
    }

    // Rebuilding resets the select, so restore the preferred choice and update any selects that follow this one
    applyPreferredOption(container);
    syncFollowers(selectId);
  }

  /**
   * A box with data-follow-select-id copies that select's value until the shopper picks one in this box.
   * After that, it keeps the shopper's own choice.
   * If the preferred store has no stock here, nothing is chosen for the shopper: an empty required
   * option is selected and a note names the store, so the add asks for a choice.
   * @param {HTMLElement} container
   */
  function applyPreferredOption(container) {
    const followSelectId = container.dataset.followSelectId;
    const select = container.querySelector('select');
    if (!followSelectId || !(select instanceof HTMLSelectElement) || select.disabled) return;

    const followedSelect = document.getElementById(followSelectId);
    const preferredValue =
      container.dataset.shopperValue ??
      (followedSelect instanceof HTMLSelectElement ? followedSelect.value : undefined);
    const preferredOption = Array.from(select.options).find((option) => option.value === preferredValue);
    const isPreferredStoreUnavailable = Boolean(preferredOption?.disabled);

    if (isPreferredStoreUnavailable && preferredValue) {
      showStoreUnavailable(container, select, preferredValue);
      return;
    }

    clearStoreUnavailable(container, select);
    if (preferredOption && preferredValue) select.value = preferredValue;
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLSelectElement} select
   * @param {string} storeValue - The option value, e.g. "pickup Houston Curbside Pickup"
   */
  function showStoreUnavailable(container, select, storeValue) {
    let placeholderOption = select.querySelector('option[data-placeholder]');
    if (!(placeholderOption instanceof HTMLOptionElement)) {
      placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.disabled = true;
      placeholderOption.dataset.placeholder = '';
      placeholderOption.text = container.dataset.placeholderText || '';
      select.prepend(placeholderOption);
    }
    placeholderOption.selected = true;
    select.required = true;

    const note = container.querySelector('[data-pickup-unavailable-note]');
    if (note instanceof HTMLElement) {
      const storeName = storeValue.replace(/^pickup /, '');
      const noteText = note.querySelector('[data-pickup-unavailable-text]') || note;
      noteText.textContent = (container.dataset.unavailableText || '').replace('[store]', storeName);
      note.hidden = false;
      select.setAttribute('aria-describedby', note.id);
    }
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLSelectElement} select
   */
  function clearStoreUnavailable(container, select) {
    select.querySelector('option[data-placeholder]')?.remove();
    select.required = false;
    select.removeAttribute('aria-describedby');

    const note = container.querySelector('[data-pickup-unavailable-note]');
    if (note instanceof HTMLElement) note.hidden = true;
  }

  /**
   * @param {string} selectId
   */
  function syncFollowers(selectId) {
    document.querySelectorAll(`[data-follow-select-id="${CSS.escape(selectId)}"]`).forEach((container) => {
      if (container instanceof HTMLElement) applyPreferredOption(container);
    });
  }

  /**
   * A shopper's pick in a following box stops it following. A pick anywhere else updates its followers.
   * @param {Event} event
   */
  function handleSelectChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || select.name !== 'properties[Shipping Option]') return;

    const followingContainer = select.closest('[data-follow-select-id]');
    if (followingContainer instanceof HTMLElement) {
      followingContainer.dataset.shopperValue = select.value;
      applyPreferredOption(followingContainer);
      return;
    }
    if (select.id) syncFollowers(select.id);
  }

  /**
   * Single delegated listener — re-queries live holders so morphs cannot orphan updates.
   * @param {Event} event
   */
  function handleVariantUpdate(event) {
    const detail = /** @type {{ detail?: { data?: { productId?: string }, resource?: { id?: string|number, available?: boolean } } }} */ (
      event
    ).detail;
    const eventProductId = detail?.data?.productId;
    const variantId = detail?.resource?.id;
    const variantAvailable = detail?.resource?.available;

    const holders = document.querySelectorAll('.js-pickup-select-holder');
    holders.forEach((holder) => {
      if (!(holder instanceof HTMLElement) || !holder.isConnected) return;

      const container = holder.closest('.pickup-locations');
      const productId = container?.getAttribute('data-product-id') || '';
      if (productId && eventProductId && String(eventProductId) !== productId) {
        // Another product's update can morph this box back to its empty server markup, so rebuild it
        const wasReset = !holder.querySelector('select') && holder.style.display !== 'none' && !container?.hidden;
        if (wasReset) refreshPickupHolder(holder);
        return;
      }

      refreshPickupHolder(holder, { variantId, variantAvailable });
    });
  }

  if (!window.__pickupVariantListenerBound) {
    window.__pickupVariantListenerBound = true;
    document.addEventListener('variant:update', handleVariantUpdate);
    document.addEventListener('change', handleSelectChange);
  }

  /**
   * Initialize / refresh pickup dropdowns within a root (document or modal content).
   * Safe to call repeatedly after morph; does not stack listeners.
   * @param {ParentNode} [root]
   */
  window.initPickupAvailability = function (root) {
    const scope = root || document;
    if (!scope || !scope.querySelectorAll) return;

    const holders = scope.querySelectorAll('.js-pickup-select-holder');
    holders.forEach((holder) => {
      if (!(holder instanceof HTMLElement)) return;
      refreshPickupHolder(holder);
    });
  };
})();
